import { styled } from 'baseui';
import { Select, SelectProps, SIZE, TYPE } from 'baseui/select';
import Fuse from 'fuse.js';
import React from 'react';
import { StyleObject } from 'styletron-standard';
import { dialogBackdropStyles } from '../../../Components/Dialog/styles';
import { optionContentStyle, popOverBodyStyle } from '../../../Components/Dropdown/styles';
import { MIcon } from '../../../Components/Icon';
import { borderStyle, marginSize, paddingSize } from '../../../Utilities/utils/Theming';
import { useKeyboardManagerState } from '../hooks/useKeyboardManagerState';
import { KeyboardShortcutState } from '../redux/types';
import { StringWithMatches } from './StringWithMatches';

const Container = styled('div', {
	position: 'relative',
	width: '350px',
	marginTop: '10vh',
	opacity: 1,
	zIndex: 2500,
});

const Aside = styled('aside', {
	position: 'fixed',
	top: 0,
	bottom: 0,
	left: 0,
	right: 0,
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'center',
	zIndex: 100,
});

const Backdrop = styled('div', {
	position: 'absolute',
	top: 0,
	bottom: 0,
	right: 0,
	left: 0,
	...dialogBackdropStyles,
});

// const Transcript = styled('span', {
// 	position: 'absolute',
// 	left: '36px',
// 	right: '48px',
// 	top: '50%',
// 	transform: 'translateY(-50%)',
// 	display: 'block',
// 	whiteSpace: 'nowrap',
// 	overflow: 'hidden',
// 	textOverflow: 'ellipsis',
// 	zIndex: 1600,
// });

const CustomSelect: React.FC<SelectProps & {}> = ({ ...rest }) => {
	return (
		<Select
			type={TYPE.search}
			clearable
			size={SIZE.large}
			overrides={{
				Root: {
					style: {
						...popOverBodyStyle(),
						...paddingSize('0', '0', '0', '0'),
					} as any,
					props: {
						'data-epo': 'epo-dropdown', // TODO add version suffix
					},
				},
				ClearIcon: {
					component: () => <MIcon icon="clear" size="18px" color="var(--epods--color-text-primary)" />,
				},
				ControlContainer: {
					style: {
						paddingRight: '10px',
						display: 'flex',
						alignItems: 'center',
						...borderStyle('none'),
					} as StyleObject,
				},
				SearchIcon: {
					component: () => <MIcon icon={'search'} size="18px" color={'#0B0C0C'} />,
				},
				SelectArrow: {
					style: { display: 'none' },
				},
				Popover: {
					props: {
						overrides: {
							Body: {
								style: popOverBodyStyle(),
								props: {
									'data-epo': 'epo-dropdown',
								},
							},
						},
					},
				},
				OptionContent: {
					style: ({ $selected, $isHighlighted, $theme }: any) => {
						return {
							...(optionContentStyle($theme, $selected, $isHighlighted, rest) as any),
							overflow: 'hidden',
							whiteSpace: 'nowrap',
							textOverflow: 'ellipsis',
						};
					},
				},
				DropdownListItem: {
					style: ({ $selected, $isHighlighted, $theme }: any) => {
						return {
							...marginSize('0', '0', '0', '0'),
							...paddingSize('0', '0', '0', '0'),
							fontFamily: 'var(--epods--font-secondary)',
							backgroundColor: 'transparent',
						};
					},
				},
				Dropdown: {
					style: {
						boxShadow: 'none',
					},
				},
			}}
			{...rest}
		/>
	);
};

export const ShortcutsSpotlight: React.FC<{
	initialFocusOnMic?: boolean;
	onComplete: () => void;
}> = ({ onComplete }) => {
	const { state } = useKeyboardManagerState();

	const controlRef = React.useRef<HTMLInputElement | HTMLDivElement>(null);
	const shortcuts = Object.values(state.shortcuts);
	const fuse = new Fuse(shortcuts, {
		includeMatches: true,
		shouldSort: false,
		minMatchCharLength: 2,
		findAllMatches: false,
		keys: ['name'],
		ignoreLocation: false,
		threshold: 0.5,
	});
	const [results, setResults] = React.useState<Array<Fuse.FuseResult<KeyboardShortcutState>>>([]);
	// TODO: stackoverflow.com/questions/63288798/typescript-error-on-importing-fuse-js-library

	// const [isLoading, setIsLoading] = React.useState(false); // TODO: review 3.0 @Joao
	/* React.useEffect(() => {
		if (transcript.indexOf('stop') !== -1) {
			resetTranscript();
			SpeechRecognition.stopListening();
			setIsListening(false);
			onComplete();
		}
	}, [onComplete, resetTranscript, transcript]);

	React.useLayoutEffect(() => {
		if (initialFocusOnMic) {
			// @ts-ignore
			const button: HTMLButtonElement | undefined | null =
				controlRef.current?.parentElement?.parentElement?.nextElementSibling?.nextElementSibling;
			if (button) {
				setTimeout(() => {
					button.click();
				}, 100);
			}
		}
	}, [initialFocusOnMic, controlRef, resetTranscript]); */

	const handleInputChange = function (term: string) {
		if (!term) {
			setResults([]);
			return;
		}
		const nextOptions = fuse.search(term);
		setResults(nextOptions);
	};

	return (
		<Aside>
			<Backdrop />
			<Container>
				<CustomSelect
					autoFocus
					aria-label="Type shortcut label or use voice input"
					controlRef={controlRef}
					escapeClearsValue={true}
					placeholder={'Type shortcut label or use voice input'}
					options={results}
					filterOptions={(options, filterValue) => {
						return options.filter(resultItem => {
							if (['START_VOICE_SHORTCUT', 'TOGGLE_SHORTCUTS_SPOTLIGHT'].includes(resultItem.item.id)) {
								return false;
							}
							return resultItem.item.handler;
						});
					}}
					onInputChange={event => {
						// @ts-ignore
						const target = event.target as HTMLInputElement;
						handleInputChange(target.value);
					}}
					onChange={({ value }) => {
						if (value.length === 1) {
							const item = (value[0] as Fuse.FuseResult<KeyboardShortcutState>).item;
							if (item.handler) {
								item.handler();
								onComplete();
							}
						}
					}}
					getOptionLabel={({ option, optionState }) => {
						const resultItem = option as Fuse.FuseResult<KeyboardShortcutState>;

						return (
							<StringWithMatches
								text={resultItem.item.name}
								matches={
									[...Array.from(resultItem.matches ?? [{ indices: [] }])[0].indices] as Array<
										[number, number]
									>
								}
							/>
						);
					}}
				/>
			</Container>
		</Aside>
	);
};
